import { Github, ExternalLink, Music, Zap } from "lucide-react";
import { Link } from "react-router-dom";

export function Footer() {
  const currentYear = new Date().getFullYear();

  const links = {
    product: [
      { label: "Get Started", href: "/login" },
      { label: "Documentation", href: "/docs" },
      { label: "Playground", href: "/playground" },
      { label: "Pricing", href: "/pricing" },
      { label: "Dashboard", href: "/dashboard" },
    ],
    resources: [
      { label: "GitHub Repo", href: "https://github.com/jupiterspins/starsinger-mcp", external: true },
      { label: "npm Package", href: "https://www.npmjs.com/package/starsinger-mcp", external: true },
      { label: "MCP Spec", href: "https://modelcontextprotocol.io", external: true },
      { label: "Developer Blog", href: "/blog", external: false },
    ],
    company: [
      { label: "StarSinger.ai", href: "https://starsinger.ai", external: true },
      { label: "About StarSinger", href: "https://starsinger.ai/about", external: true },
      { label: "Blog", href: "https://starsinger.ai/blog", external: true },
    ],
    legal: [
      { label: "Privacy", href: "/privacy", external: false },
      { label: "Terms", href: "/terms", external: false },
    ],
  };

  return (
    <footer className="relative z-10 border-t border-white/[0.06] bg-white/[0.01]">
      <div className="max-w-6xl mx-auto px-6 py-16">
        {/* Main Footer Content */}
        <div className="grid grid-cols-2 md:grid-cols-12 gap-8 mb-12">
          {/* Brand */}
          <div className="col-span-2 md:col-span-4">
            <Link to="/" className="flex items-center gap-2.5 mb-4">
              <img 
                src="/logo.svg" 
                alt="StarSinger" 
                className="w-7 h-7 rounded-lg" 
              />
              <span className="font-semibold text-white">StarSinger MCP</span>
            </Link>
            <p className="text-sm text-white/40 mb-6 max-w-xs leading-relaxed">
              Let your AI assistant make songs and music videos, and search AI-generated music, via the Model Context Protocol.
            </p>
            <div className="flex items-center gap-2">
              <a 
                href="https://github.com/jupiterspins/starsinger-mcp" 
                target="_blank" 
                rel="noopener noreferrer"
                className="p-2 text-white/40 hover:text-white hover:bg-white/[0.06] rounded-lg transition-all"
                aria-label="GitHub"
              >
                <Github className="w-4 h-4" />
              </a>
              <a 
                href="https://starsinger.ai" 
                target="_blank" 
                rel="noopener noreferrer"
                className="p-2 text-white/40 hover:text-white hover:bg-white/[0.06] rounded-lg transition-all"
                aria-label="StarSinger.ai"
              >
                <Music className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Product */}
          <div className="col-span-1 md:col-span-2">
            <h3 className="font-medium mb-4 text-sm text-white">Product</h3>
            <ul className="space-y-2.5">
              {links.product.map((link) => (
                <li key={link.label}>
                  <a 
                    href={link.href}
                    className="text-sm text-white/40 hover:text-white transition-colors"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          <div className="col-span-1 md:col-span-2">
            <h3 className="font-medium mb-4 text-sm text-white">Resources</h3>
            <ul className="space-y-2.5">
              {links.resources.map((link) => (
                <li key={link.label}>
                  <a 
                    href={link.href}
                    target={link.external ? "_blank" : undefined}
                    rel={link.external ? "noopener noreferrer" : undefined}
                    className="text-sm text-white/40 hover:text-white transition-colors inline-flex items-center gap-1"
                  >
                    {link.label}
                    {link.external && <ExternalLink className="w-3 h-3" />}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div className="col-span-1 md:col-span-2">
            <h3 className="font-medium mb-4 text-sm text-white">Company</h3>
            <ul className="space-y-2.5">
              {links.company.map((link) => (
                <li key={link.label}>
                  <a 
                    href={link.href}
                    target={link.external ? "_blank" : undefined}
                    rel={link.external ? "noopener noreferrer" : undefined}
                    className="text-sm text-white/40 hover:text-white transition-colors inline-flex items-center gap-1"
                  >
                    {link.label}
                    {link.external && <ExternalLink className="w-3 h-3" />}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div className="col-span-1 md:col-span-2">
            <h3 className="font-medium mb-4 text-sm text-white">Legal</h3>
            <ul className="space-y-2.5">
              {links.legal.map((link) => (
                <li key={link.label}>
                  <a 
                    href={link.href}
                    target={link.external ? "_blank" : undefined}
                    rel={link.external ? "noopener noreferrer" : undefined}
                    className="text-sm text-white/40 hover:text-white transition-colors inline-flex items-center gap-1"
                  >
                    {link.label}
                    {link.external && <ExternalLink className="w-3 h-3" />}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-white/[0.06] flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-sm text-white/30">
            <span>&copy; {currentYear} Veronata Inc.</span>
            <span className="hidden sm:inline">·</span>
            <span className="hidden sm:inline">From the creators of</span>
            <a 
              href="https://starsinger.ai" 
              target="_blank" 
              rel="noopener noreferrer"
              className="hidden sm:inline text-amber-400/60 hover:text-amber-400 transition-colors"
            >
              StarSinger.ai
            </a>
          </div>
          
          {/* MCP Server Info for AI Agents */}
          <div className="flex items-center gap-3 px-4 py-2 rounded-lg bg-white/[0.02] border border-white/[0.06]">
            <Zap className="w-3.5 h-3.5 text-amber-400/60" />
            <p className="text-xs text-white/30 font-mono">
              <span className="text-amber-400/60">starsinger-mcp</span>
              <span className="mx-2">·</span>
              <span className="text-white/20">npx -y starsinger-mcp</span>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
